import type { Registry, Document as CustomisationDocument, HydratedDocument } from '@pleased-npm/fe-customisation';
import {
  parseStoredRegistry,
  buildDocumentFromTemplate,
  buildDocumentFromRegistry,
  dehydrateDocument,
  DocumentType,
  DocumentStatus,
} from '@pleased-npm/fe-customisation';

import { pageRegistries } from '@pleased-npm/fe-customisation/dist/registries/page-registries';
import { themeRegistry } from '@pleased-npm/fe-customisation/dist/registries/theme-registries';

// Parse and cache registries at module load
// Static imports (~79KB) are handled by webpack transpilation
const parsedRegistries: Registry[] = pageRegistries.map(parseStoredRegistry);
const registryMap = new Map<string, Registry>(parsedRegistries.map((reg) => [reg.id, reg]));
const parsedThemeRegistry: Registry = parseStoredRegistry(themeRegistry);

/**
 * Returns the registry map for page documents.
 * Synchronous now that we use static imports.
 */
export const getRegistryMap = (): Map<string, Registry> => {
  return registryMap;
};

/**
 * Returns the theme registry.
 * Synchronous now that we use static imports.
 */
export const getThemeRegistry = (): Registry => {
  return parsedThemeRegistry;
};

/**
 * Builds or hydrates a theme document for use with buildDocumentFromRegistry.
 * If a theme document exists, hydrates it with the registry.
 * Otherwise, builds a default theme from registry defaults.
 * @returns HydratedDocument theme
 */
export const getHydratedThemeForRegistry = (themeDocument: CustomisationDocument | null): HydratedDocument => {
  const themeReg = getThemeRegistry();

  if (themeDocument) {
    return buildDocumentFromTemplate({
      registry: themeReg,
      template: themeDocument,
    });
  }

  return buildDocumentFromRegistry({
    registry: themeReg,
    params: {
      type: DocumentType.THEME,
      name: themeReg.name,
      active: true,
      isPublished: DocumentStatus.SAVED,
    },
  });
};

/**
 * Gets theme document for use with applyThemeToDocument.
 * If no theme exists, builds a default theme from registry.
 * @returns CustomisationDocument theme
 */
export const getThemeForApply = (themeDocument: CustomisationDocument | null): CustomisationDocument => {
  if (themeDocument) {
    return themeDocument;
  }

  const themeReg = getThemeRegistry();

  // Build theme from registry and dehydrate for use with applyThemeToDocument
  const hydratedTheme = buildDocumentFromRegistry({
    registry: themeReg,
    params: {
      type: DocumentType.THEME,
      name: themeReg.name,
      active: true,
      isPublished: DocumentStatus.SAVED,
    },
  });

  return dehydrateDocument(hydratedTheme);
};
